import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";
import { AccountBalanceOutlined } from "@mui/icons-material";
import PartnerInfo from "../../partner-info";

export type Option = {
  label: string;
  value: string;
  id?: number;
  mobile?: string;
  type?: string;
  profilePic?: string;
  address?: string;
};

type Props = {
  options: Option[];
  selected: Option | null;
  onChange: (option: Option | null) => void;
  placeholder?: string;
  fromMainScreen?: boolean;
  fromDeleteMarketScreen?: boolean;
};

export default function SingleSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = "Search...",
  fromMainScreen = false,
  fromDeleteMarketScreen = false,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setOpen(false);
        setSearch("");
      }
    };
    window.addEventListener("click", handleClickOutside, true);
    return () => window.removeEventListener("click", handleClickOutside, true);
  }, []);

  // Normalize text for search
  const normalizeText = (str: string = "") =>
    str.toString().toLowerCase().replace(/\s+/g, "") ?? "";

  const filtered = Array.isArray(options)
    ? options.filter((opt) => {
        const text = normalizeText(search);
        return (
          normalizeText(opt?.label).includes(text) ||
          normalizeText(opt?.value).includes(text) ||
          opt?.id?.toString().includes(search.trim())
        );
      })
    : [];

  const selectOption = (option: Option) => {
    if (selected?.id === option.id) {
      onChange(null);
    } else {
      onChange(option);
    }
    setOpen(false);
    setSearch("");
  };

  // Detect drop-up
  useEffect(() => {
    if (open && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 240;
      setDropUp(spaceBelow < dropdownHeight);
    }
  }, [open]);
  return (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className="border rounded-md flex items-center px-2 py-3 cursor-pointer bg-white gap-2"
        onClick={() => setOpen((prev) => !prev)}
      >
        {!open && (
          <div className="flex flex-wrap gap-1 flex-1 text-sm">
            {selected ? (
              <span className="bg-[#D7F3DD] rounded-full px-2 py-1 flex items-center gap-1">
                {selected.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                  className="text-red-600"
                >
                  ✕
                </button>
              </span>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
        )}

        {open && (
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className="flex-1 outline-none cursor-text text-sm"
            autoFocus
          />
        )}

        <FaChevronDown
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          size={10}
          color="#16a34a"
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={`${
            fromDeleteMarketScreen ? "fixed w-[35%]" : "absolute w-full"
          } z-10 bg-white border rounded-md shadow-lg ${
            dropUp ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          <div className="max-h-60 overflow-auto text-sm">
            {filtered.length > 0 ? (
              filtered.map((option) => (
                <div
                  key={option.id}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[#D7F3DD] border-b ${
                    selected?.id === option.id ? "bg-[#D7F3DD]" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectOption(option);
                  }}
                >
                  {fromMainScreen ? (
                    <div className="flex items-center gap-2 fs-14 color-text-primary font-semibold">
                      <AccountBalanceOutlined
                        style={{ fontSize: 22, color: "color-sub-text" }}
                      />
                      <span>{option?.label}</span>
                    </div>
                  ) : (
                    <PartnerInfo
                      profilePic={option?.profilePic || ""}
                      name={option?.label}
                      shortName={""}
                      partnerType={option?.type}
                      partnerId={option?.id}
                      partnerMobile={option?.mobile || ""}
                      fromLoansParty={true}
                      partyName={option?.address}
                    />
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-center text-gray-500">
                No data found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
